package songwritersnotebook.app.components

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.filled.FavoriteBorder
import androidx.compose.material.icons.filled.PlayArrow
import androidx.compose.material.icons.filled.Shuffle
import androidx.compose.material.icons.filled.Piano
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.painterResource
import songwritersnotebook.app.adaptiveSheetWidth
import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.ChordProgression
import songwritersnotebook.app.ui.theme.BlueAccent
import songwritersnotebook.app.ui.theme.GoldAccent
import songwritersnotebook.app.utils.ChordUtils
import songwritersnotebookapp.composeapp.generated.resources.Res
import songwritersnotebookapp.composeapp.generated.resources.ic_guitar

enum class ChordSource {
    SEQUENCE, DIATONIC, LIBRARY
}

data class SelectedChordItem(
    val chordName: String,
    val source: ChordSource,
    val sequenceIndex: Int? = null
)

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ProgressionBuilderSheet(
    initialProgression: ChordProgression? = null,
    availableChords: Map<String, ChordDefinition>,
    onDismiss: () -> Unit,
    onSave: (ChordProgression) -> Unit,
    onPlayChord: (String, String) -> Unit, // chordName, instrument
    onEditChord: (String) -> Unit,
    onCreateChord: () -> Unit,
    selectedInstrument: String,
    onInstrumentChange: (String) -> Unit
) {
    var progressionName by remember { mutableStateOf(initialProgression?.name ?: "New Progression") }
    var isFavorite by remember { mutableStateOf(initialProgression?.isFavorite ?: false) }
    
    var selectedKeyRoot by remember { mutableStateOf("C") }
    var selectedKeyScale by remember { mutableStateOf("Major") }
    var rootDropdownExpanded by remember { mutableStateOf(false) }
    var scaleDropdownExpanded by remember { mutableStateOf(false) }

    // Unified selection state
    var selectedItem by remember { mutableStateOf<SelectedChordItem?>(null) }
    
    // The sequence of chords in the progression being built
    var chordSequence by remember { 
        mutableStateOf(initialProgression?.chordNames ?: listOf("C", "G"))
    }

    val sheetState = rememberModalBottomSheetState(skipPartiallyExpanded = true)

    val diatonicChords = remember(selectedKeyRoot, selectedKeyScale) {
        ChordUtils.getDiatonicChords(selectedKeyRoot, selectedKeyScale)
    }

    ModalBottomSheet(
        onDismissRequest = onDismiss,
        sheetState = sheetState,
    ) {
        Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.TopCenter) {
            Column(
                modifier = Modifier
                    .adaptiveSheetWidth()
                    .navigationBarsPadding()
            ) {
                // Header
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    Text(
                        if (initialProgression != null) "Edit Progression" else "Create Progression",
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )
                    
                    Row(
                        modifier = Modifier
                            .height(32.dp)
                            .border(
                                width = 1.dp,
                                color = MaterialTheme.colorScheme.outline,
                                shape = RoundedCornerShape(50)
                            ),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        val pianoColors = if (selectedInstrument == "piano") ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)
                        val guitarColors = if (selectedInstrument == "guitar") ButtonDefaults.buttonColors() else ButtonDefaults.outlinedButtonColors(contentColor = MaterialTheme.colorScheme.onSurfaceVariant)

                        Button(
                            onClick = { onInstrumentChange("piano") },
                            shape = RoundedCornerShape(topStartPercent = 50, bottomStartPercent = 50),
                            modifier = Modifier.fillMaxHeight().width(48.dp),
                            contentPadding = PaddingValues(0.dp),
                            colors = pianoColors
                        ) {
                            Icon(Icons.Filled.Piano, contentDescription = "Piano", modifier = Modifier.size(18.dp))
                        }

                        Box(modifier = Modifier.fillMaxHeight().width(1.dp).background(MaterialTheme.colorScheme.outline))

                        Button(
                            onClick = { onInstrumentChange("guitar") },
                            shape = RoundedCornerShape(topEndPercent = 50, bottomEndPercent = 50),
                            modifier = Modifier.fillMaxHeight().width(48.dp),
                            contentPadding = PaddingValues(0.dp),
                            colors = guitarColors
                        ) {
                            Icon(painter = painterResource(Res.drawable.ic_guitar), contentDescription = "Guitar", modifier = Modifier.size(18.dp))
                        }
                    }

                    IconButton(onClick = onDismiss) {
                        Icon(Icons.Default.Close, contentDescription = "Close")
                    }
                }

                // 1. Name
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    OutlinedTextField(
                        value = progressionName,
                        onValueChange = { progressionName = it },
                        label = { Text("Name") },
                        modifier = Modifier.weight(1f),
                        singleLine = true
                    )
                    IconButton(onClick = { isFavorite = !isFavorite }) {
                        Icon(
                            if (isFavorite) Icons.Default.Favorite else Icons.Default.FavoriteBorder,
                            contentDescription = "Favorite Progression",
                            tint = if (isFavorite) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
                
                Spacer(Modifier.height(8.dp))

                // 2. Key and Quality Selection
                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    ExposedDropdownMenuBox(
                        expanded = rootDropdownExpanded,
                        onExpandedChange = { rootDropdownExpanded = it },
                        modifier = Modifier.weight(1f)
                    ) {
                        OutlinedTextField(
                            value = selectedKeyRoot,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Root") },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = rootDropdownExpanded) },
                            modifier = Modifier.menuAnchor()
                        )
                        ExposedDropdownMenu(
                            expanded = rootDropdownExpanded,
                            onDismissRequest = { rootDropdownExpanded = false }
                        ) {
                            ChordUtils.ROOT_NOTES.forEach { root ->
                                DropdownMenuItem(
                                    text = { Text(root) },
                                    onClick = {
                                        selectedKeyRoot = root
                                        rootDropdownExpanded = false
                                        if (selectedItem?.source == ChordSource.DIATONIC) {
                                            selectedItem = null
                                        }
                                    }
                                )
                            }
                        }
                    }

                    ExposedDropdownMenuBox(
                        expanded = scaleDropdownExpanded,
                        onExpandedChange = { scaleDropdownExpanded = it },
                        modifier = Modifier.weight(1f)
                    ) {
                        OutlinedTextField(
                            value = selectedKeyScale,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Scale") },
                            trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = scaleDropdownExpanded) },
                            modifier = Modifier.menuAnchor()
                        )
                        ExposedDropdownMenu(
                            expanded = scaleDropdownExpanded,
                            onDismissRequest = { scaleDropdownExpanded = false }
                        ) {
                            listOf("Major", "Minor").forEach { scale ->
                                DropdownMenuItem(
                                    text = { Text(scale) },
                                    onClick = {
                                        selectedKeyScale = scale
                                        scaleDropdownExpanded = false
                                        if (selectedItem?.source == ChordSource.DIATONIC) {
                                            selectedItem = null
                                        }
                                    }
                                )
                            }
                        }
                    }
                }

                Spacer(Modifier.height(24.dp))

                // 3. Your Sequence
                Text(
                    "Your Sequence",
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(horizontal = 16.dp)
                )
                
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(vertical = 8.dp, horizontal = 16.dp)
                        .horizontalScroll(rememberScrollState()),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    chordSequence.forEachIndexed { index, chordName ->
                        val isSelected = selectedItem?.source == ChordSource.SEQUENCE && selectedItem?.sequenceIndex == index
                        val containerColor = if (isSelected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.primaryContainer
                        val contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onPrimaryContainer
                        
                        Box(
                            modifier = Modifier
                                .background(containerColor, RoundedCornerShape(8.dp))
                                .clickable { 
                                    selectedItem = SelectedChordItem(chordName, ChordSource.SEQUENCE, index)
                                    onPlayChord(chordName, selectedInstrument) 
                                }
                        ) {
                            Text(
                                chordName, 
                                modifier = Modifier.padding(horizontal = 16.dp, vertical = 10.dp),
                                fontFamily = FontFamily.Monospace, 
                                color = contentColor
                            )
                        }
                    }
                    
                    IconButton(
                        onClick = { 
                            val randomChord = diatonicChords.random().chordName
                            chordSequence = chordSequence + randomChord
                            selectedItem = SelectedChordItem(randomChord, ChordSource.SEQUENCE, chordSequence.size - 1)
                            onPlayChord(randomChord, selectedInstrument)
                        },
                        modifier = Modifier.background(MaterialTheme.colorScheme.surfaceVariant, RoundedCornerShape(8.dp))
                    ) {
                        Icon(Icons.Default.Shuffle, contentDescription = "Shuffle Next Chord")
                    }
                }

                Spacer(Modifier.height(16.dp))

                // Action Bar (Persistent, right below Sequence)
                Surface(
                    color = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 8.dp),
                        horizontalArrangement = Arrangement.SpaceEvenly,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        val canAdd = selectedItem != null
                        val canEdit = selectedItem != null
                        val canDelete = selectedItem?.source == ChordSource.SEQUENCE || selectedItem?.source == ChordSource.LIBRARY
                        
                        TextButton(
                            onClick = {
                                selectedItem?.let { item ->
                                    chordSequence = chordSequence + item.chordName
                                    selectedItem = SelectedChordItem(item.chordName, ChordSource.SEQUENCE, chordSequence.size - 1)
                                }
                            },
                            enabled = canAdd
                        ) {
                            Icon(Icons.Default.Add, "Add")
                            Spacer(Modifier.width(4.dp))
                            Text("Add")
                        }
                        
                        TextButton(
                            onClick = {
                                selectedItem?.let { item ->
                                    onEditChord(item.chordName)
                                }
                            },
                            enabled = canEdit
                        ) {
                            Icon(Icons.Default.Edit, "Edit")
                            Spacer(Modifier.width(4.dp))
                            Text("Edit")
                        }
                        
                        TextButton(
                            onClick = {
                                selectedItem?.let { item ->
                                    if (item.source == ChordSource.SEQUENCE && item.sequenceIndex != null) {
                                        val mutableSeq = chordSequence.toMutableList()
                                        mutableSeq.removeAt(item.sequenceIndex)
                                        chordSequence = mutableSeq
                                        selectedItem = null
                                    } else if (item.source == ChordSource.LIBRARY) {
                                        // TODO: if we need to support deleting library chords directly from here
                                    }
                                }
                            },
                            enabled = canDelete,
                            colors = ButtonDefaults.textButtonColors(contentColor = MaterialTheme.colorScheme.error)
                        ) {
                            Icon(Icons.Default.Delete, "Delete")
                            Spacer(Modifier.width(4.dp))
                            Text("Delete")
                        }
                    }
                }

                Spacer(Modifier.height(24.dp))

                // 4. Diatonic Chords
                Text(
                    "Diatonic Chords",
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(horizontal = 16.dp)
                )
                Spacer(Modifier.height(8.dp))

                Row(
                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp),
                    horizontalArrangement = Arrangement.SpaceBetween
                ) {
                    diatonicChords.forEach { diatonic ->
                        val isSelected = selectedItem?.source == ChordSource.DIATONIC && selectedItem?.chordName == diatonic.chordName
                        val hasCustom = availableChords.containsKey(diatonic.chordName) && availableChords[diatonic.chordName]?.isCustom == true
                        val isInUse = chordSequence.contains(diatonic.chordName)
                        val borderColor = if (isSelected) MaterialTheme.colorScheme.primary else if (isInUse) GoldAccent else if (hasCustom) BlueAccent else Color.Transparent
                        val containerColor = if (isSelected) MaterialTheme.colorScheme.primaryContainer else if (isInUse) GoldAccent else MaterialTheme.colorScheme.surfaceVariant
                        val contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimaryContainer else if (isInUse) Color.Black else MaterialTheme.colorScheme.onSurfaceVariant

                        Column(horizontalAlignment = Alignment.CenterHorizontally, modifier = Modifier.weight(1f)) {
                            Text(diatonic.numeral, style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant)
                            OutlinedButton(
                                onClick = { 
                                    if (isSelected) {
                                        // Tap to deselect
                                        selectedItem = null
                                    } else {
                                        // First click: Select and play
                                        selectedItem = SelectedChordItem(diatonic.chordName, ChordSource.DIATONIC)
                                        onPlayChord(diatonic.chordName, selectedInstrument)
                                    }
                                },
                                contentPadding = PaddingValues(0.dp),
                                modifier = Modifier.padding(horizontal = 2.dp).fillMaxWidth().aspectRatio(1f),
                                shape = RoundedCornerShape(8.dp),
                                border = BorderStroke(if (isSelected) 2.dp else 1.dp, borderColor),
                                colors = ButtonDefaults.outlinedButtonColors(
                                    containerColor = containerColor,
                                    contentColor = contentColor
                                )
                            ) {
                                Text(diatonic.chordName, style = MaterialTheme.typography.bodySmall, fontFamily = FontFamily.Monospace, fontSize = 12.sp)
                            }
                        }
                    }
                }

                Spacer(Modifier.height(24.dp))

                // 5. Library Chords
                Text(
                    "Library Chords",
                    style = MaterialTheme.typography.titleMedium,
                    modifier = Modifier.padding(horizontal = 16.dp)
                )
                Spacer(Modifier.height(8.dp))
                LazyRow(
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(horizontal = 16.dp),
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    items(availableChords.keys.sorted()) { chord ->
                        val isSelected = selectedItem?.source == ChordSource.LIBRARY && selectedItem?.chordName == chord
                        val isCustom = availableChords[chord]?.isCustom == true
                        val isInUse = chordSequence.contains(chord)
                        
                        val containerColor = if (isSelected) MaterialTheme.colorScheme.primaryContainer else if (isInUse) GoldAccent else if (isCustom) BlueAccent else Color.Transparent
                        val contentColor = if (isSelected) MaterialTheme.colorScheme.onPrimaryContainer else if (isInUse || isCustom) Color.Black else MaterialTheme.colorScheme.onSurface
                        val borderColor = if (isSelected) MaterialTheme.colorScheme.primary else if (isInUse) GoldAccent else if (isCustom) BlueAccent else MaterialTheme.colorScheme.outline
                        
                        OutlinedButton(
                            onClick = {
                                if (isSelected) {
                                    selectedItem = null
                                } else {
                                    selectedItem = SelectedChordItem(chord, ChordSource.LIBRARY)
                                    onPlayChord(chord, selectedInstrument)
                                }
                            },
                            shape = RoundedCornerShape(8.dp),
                            border = BorderStroke(if (isSelected) 2.dp else 1.dp, borderColor),
                            colors = ButtonDefaults.outlinedButtonColors(
                                containerColor = containerColor,
                                contentColor = contentColor
                            )
                        ) {
                            Text(chord, fontFamily = FontFamily.Monospace)
                        }
                    }
                    item {
                        OutlinedButton(
                            onClick = onCreateChord,
                            shape = RoundedCornerShape(8.dp)
                        ) {
                            Icon(Icons.Default.Add, "Create New")
                            Spacer(Modifier.width(4.dp))
                            Text("Create")
                        }
                    }
                }
                
                Spacer(Modifier.weight(1f))
                
                // Footer
                Row(
                    modifier = Modifier.fillMaxWidth().padding(16.dp),
                    horizontalArrangement = Arrangement.End
                ) {
                    Button(
                        onClick = {
                            val prog = initialProgression?.copy(
                                name = progressionName,
                                chordNames = chordSequence,
                                isFavorite = isFavorite
                            ) ?: ChordProgression(
                                name = progressionName,
                                chordNames = chordSequence,
                                isFavorite = isFavorite
                            )
                            onSave(prog)
                        },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("Save Progression")
                    }
                }
            }
        }
    }
}
